/**
 * Guest Draft Service
 *
 * Manages the sessionStorage-based draft for the guest scheduling flow.
 * When an unauthenticated user fills in all card scheduling details and clicks
 * "Sign Up & Schedule", the draft is saved here before redirecting to Entra.
 * After sign-up + terms acceptance, the app detects the draft and shows a
 * ReviewDraftModal for final confirmation.
 *
 * sessionStorage is used because it persists across same-tab redirects
 * (the MSAL loginRedirect flow stays in the same tab) but auto-cleans
 * when the tab closes, avoiding stale orphan data.
 *
 * Note: File objects (media overrides) cannot be stored in sessionStorage.
 * The hadCustomMedia flag tells the ReviewDraftModal to prompt for re-upload.
 */

import {
  GUEST_DRAFT_STORAGE_KEY,
  GUEST_FLOWER_DRAFT_STORAGE_KEY,
  GUEST_MAILED_CARD_DRAFT_STORAGE_KEY,
} from '../constants/scheduling';

/**
 * Contact data collected during the guest scheduling flow.
 * Not yet persisted to the backend — created via API after sign-up confirmation.
 */
export interface GuestContactDraft {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  birthday?: string;
  /**
   * When false, only month/day are meaningful for {@link birthday}; matches API
   * {@code DateConstants.UnknownYear} storage after sign-up creates the contact.
   */
  birthdayYearKnown?: boolean;
  notes?: string;

  // Address fields — saved so the post-signup flow can create a complete contact
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;

  // Card preference fields — saved so the contact retains its preferences
  relationship?: string;
  preferredTone?: string;
  preferredMediaType?: string;
  preferredVisualStyle?: string;
  /** Default AI writing instructions for this contact. */
  aiInstructions?: string;
}

/**
 * Full draft capturing all state needed to resume scheduling after sign-up.
 * Stored in sessionStorage as JSON.
 */
export interface GuestSchedulingDraft {
  /** Template ID for the selected card */
  templateId: number;
  templateName: string;
  templateMediaToken?: string | null;
  templateMediaType?: string | null;
  /** Still/poster for confirmation thumb when templateMediaType is video. */
  templatePreviewImageUrl?: string | null;
  templatePosterMediaToken?: string | null;
  templateIsAnimated?: boolean;
  templateDefaultGreetingText: string;
  templateDefaultSubjectLine?: string;

  /** Event type derived from the selected template */
  eventTypeId: number;
  eventTypeName: string;
  fixedMonth?: number | null;
  fixedDay?: number | null;
  dateFieldName?: string;

  /** Recipient contact info entered by the guest */
  contact: GuestContactDraft;

  /** Scheduling details */
  scheduledDate: string;
  timeSlot: string;
  customMessage: string;
  customSubjectLine: string;

  /**
   * Whether the guest selected custom media during the flow.
   * File objects can't survive sessionStorage, so this flag tells
   * the ReviewDraftModal to prompt for re-upload.
   */
  hadCustomMedia: boolean;

  /**
   * When true, the ReviewDraftModal auto-triggers confirmation on open
   * so the user doesn't have to click "Confirm" again after sign-up.
   * Set by handleGuestSchedule when the user already reviewed and confirmed.
   */
  autoConfirm?: boolean;
}

/**
 * Saves a guest scheduling draft to sessionStorage.
 * Overwrites any existing draft.
 */
export const saveGuestDraft = (draft: GuestSchedulingDraft): void => {
  try {
    sessionStorage.setItem(GUEST_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // sessionStorage unavailable or quota exceeded
    console.error('Failed to save guest scheduling draft');
  }
};

/**
 * Loads the guest scheduling draft from sessionStorage.
 * Returns null if no draft exists or if the data is corrupted.
 */
export const loadGuestDraft = (): GuestSchedulingDraft | null => {
  try {
    const raw = sessionStorage.getItem(GUEST_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as GuestSchedulingDraft;
  } catch {
    return null;
  }
};

/**
 * Removes the guest scheduling draft from sessionStorage.
 * Called after successful scheduling or explicit discard.
 */
export const clearGuestDraft = (): void => {
  try {
    sessionStorage.removeItem(GUEST_DRAFT_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable
  }
};

/**
 * Checks whether a guest scheduling draft exists in sessionStorage.
 * Used for quick detection without parsing the full draft.
 */
export const hasGuestDraft = (): boolean => {
  try {
    return sessionStorage.getItem(GUEST_DRAFT_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
};

// ============================================================================
// Guest Flower Order Draft
//
// Mirrors the card draft system above but for flower orders.
// Uses a separate sessionStorage key so both drafts can coexist
// (user might start both a card and flower flow before signing up).
// ============================================================================

/**
 * Full draft capturing all state needed to resume a flower order after sign-up.
 * Stored in sessionStorage as JSON.
 */
export interface GuestFlowerDraft {
  /** Opaque catalog product id (preferred create identity). */
  productPublicId?: string;
  productSku: string;
  productName: string;
  productImageUrl?: string | null;
  /** Catalog list price (pre-promo). */
  productPrice: number;
  /**
   * Catalog sale / promo price when below list. Persisted so post-signup
   * {@link ReviewFlowerDraftModal} can reuse {@link FlowerPriceDisplay}
   * strikethrough without re-fetching the product.
   */
  productSalePrice?: number | null;

  eventTypeId: number;
  eventTypeName: string;

  /** Recipient contact info entered by the guest (reuses card draft shape) */
  contact: GuestContactDraft;

  deliveryDate: string;
  recipientName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;

  /**
   * Sender's phone number (mandatory for the backend FlowerOrderService).
   * Collected during the guest flow because the not-yet-created user has
   * no profile phone to fall back on. After the order is placed, the
   * ReviewFlowerDraftModal silently persists this to the new user's
   * account preferences so future orders can default to it.
   */
  senderPhone: string;

  /**
   * Sender's first and last name collected on Your Information so the
   * Review-step greeting can show the real name before signup. Optional on
   * older drafts that pre-date this field.
   */
  senderFirstName?: string;
  senderLastName?: string;

  /**
   * Sender's home address (mandatory for the Florist One CUSTOMER block —
   * see FlowerOrderService for the validation). Collected on the "Your
   * Information" step because the not-yet-created user has no profile
   * address to fall back on. ReviewFlowerDraftModal back-fills these into
   * the new user's My Account preferences after sign-up so subsequent
   * orders can default to them and AutoSend can be enabled without
   * re-entry.
   */
  senderAddressLine1: string;
  senderAddressLine2?: string;
  senderCity: string;
  senderState: string;
  senderPostalCode: string;
  /** ISO 3166-1 alpha-2 country code (e.g. "US", "CA"). */
  senderCountry: string;

  specialInstructions?: string;
  allowSubstitutions: boolean;

  /**
   * Optional custom card message entered by the guest during the flow.
   * If empty/undefined, the backend will resolve the default template
   * (with the now-known sender name) at order creation time.
   * Capped to 200 characters to match the Florist One limit.
   */
  cardMessage?: string;

  /**
   * When true, the ReviewFlowerDraftModal auto-triggers confirmation on open
   * so the user doesn't have to click "Confirm" again after sign-up.
   * Set by handleGuestSchedule when the user already reviewed and confirmed.
   */
  autoConfirm?: boolean;
}

/**
 * Saves a guest flower order draft to sessionStorage.
 * Overwrites any existing flower draft.
 */
export const saveGuestFlowerDraft = (draft: GuestFlowerDraft): void => {
  try {
    sessionStorage.setItem(GUEST_FLOWER_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    console.error('Failed to save guest flower draft');
  }
};

/**
 * Loads the guest flower order draft from sessionStorage.
 * Returns null if no draft exists or if the data is corrupted.
 */
export const loadGuestFlowerDraft = (): GuestFlowerDraft | null => {
  try {
    const raw = sessionStorage.getItem(GUEST_FLOWER_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as GuestFlowerDraft;
  } catch {
    return null;
  }
};

/**
 * Removes the guest flower order draft from sessionStorage.
 * Called after successful order placement or explicit discard.
 */
export const clearGuestFlowerDraft = (): void => {
  try {
    sessionStorage.removeItem(GUEST_FLOWER_DRAFT_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable
  }
};

/**
 * Checks whether a guest flower order draft exists in sessionStorage.
 * Used for quick detection without parsing the full draft.
 */
export const hasGuestFlowerDraft = (): boolean => {
  try {
    return sessionStorage.getItem(GUEST_FLOWER_DRAFT_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
};

// ============================================================================
// Guest Mailed Card Draft
// ============================================================================

export interface GuestMailedCardDraft {
  mailedCardArtworkId: number;
  artworkSlug: string;
  artworkName: string;
  artworkThumbnailUrl?: string | null;
  artworkPriceUsd: number;
  eventTypeId: number;
  eventTypeName: string;
  contact: GuestContactDraft;
  message: string;
  /** Legacy guest drafts stored page arrays before single-message migration. */
  messagePages?: Array<{ page: number; text: string }>;
  shippingMethod: string;
  requestedArrival: string;
  recipientFirstName: string;
  recipientLastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  senderFirstName: string;
  senderLastName: string;
  senderAddressLine1: string;
  senderAddressLine2?: string;
  senderCity: string;
  senderState: string;
  senderPostalCode: string;
  senderCountry: string;
  cancellationAcknowledged: boolean;
  /** Optional digital gift-card face value in USD (PBI #990). */
  giftCardAmountUsd?: number | null;
  autoConfirm?: boolean;
  /** Stable idempotency key minted before sign-up (#1493). */
  guestHandoffId?: string;
}

export const saveGuestMailedCardDraft = (draft: GuestMailedCardDraft): void => {
  try {
    sessionStorage.setItem(GUEST_MAILED_CARD_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    console.error('Failed to save guest mailed-card draft');
  }
};

export const loadGuestMailedCardDraft = (): GuestMailedCardDraft | null => {
  try {
    const raw = sessionStorage.getItem(GUEST_MAILED_CARD_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GuestMailedCardDraft;
    if (!parsed.message?.trim() && parsed.messagePages?.length) {
      parsed.message = parsed.messagePages[0]?.text?.trim() ?? '';
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearGuestMailedCardDraft = (): void => {
  try {
    sessionStorage.removeItem(GUEST_MAILED_CARD_DRAFT_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable
  }
};

export const hasGuestMailedCardDraft = (): boolean => {
  try {
    return sessionStorage.getItem(GUEST_MAILED_CARD_DRAFT_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
};
